/**
 * Rotating personal QR payload — Phase 1 (Jaime-approved, addendum 2026-09-12).
 *
 * The server derives a per-code secret qr_secret = HMAC(JWT_SECRET,
 * `personal-qr:${code}`) and serves it with the resident's own code via
 * GET /api/qr-access/my-codes (resident codes only). The app then signs
 * locally — JWT_SECRET itself never reaches the client and one code's secret
 * forges nothing else.
 *
 * Payload: VQR1:<code>.<step>.<sig>
 *   step = floor(now_ms / 60000)  (60s rotation)
 *   sig  = HMAC-SHA256(qr_secret, `${code}.${step}`) truncated to 16 hex chars
 *
 * Symmetry: byte-identical with src/modules/qr-access/qr-rotation.js on the
 * backend (the HMAC key is the qr_secret as a UTF-8 string, NOT decoded hex —
 * same as Node crypto.createHmac('sha256', <hex string>)). Proven in
 * tests/qr-rotation-local.test.js.
 */

export const VQR_PREFIX = 'VQR1:';
export const STEP_MS = 60 * 1000;
export const SIG_HEX_LEN = 16;

function bytesToHex(buf: ArrayBuffer): string {
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

async function hmacHex(keyString: string, message: string): Promise<string> {
  const enc = new TextEncoder();
  const key = await crypto.subtle.importKey(
    'raw',
    enc.encode(keyString),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const sig = await crypto.subtle.sign('HMAC', key, enc.encode(message));
  return bytesToHex(sig);
}

export function stepFor(nowMs = Date.now()): number {
  return Math.floor(nowMs / STEP_MS);
}

/**
 * Build the rotating payload the personal QR displays. Mirrors
 * qr-rotation.js buildPayload/deriveQrSecret/computeSig exactly.
 */
/**
 * Rotating manual-entry digits (Phase 2, 2026-09-12): the 8 decimal digits
 * shown UNDER the QR for guards who type the code manually. Derived from the
 * SAME signature as the VQR1 payload:
 *   digits = decimal(sig_hex.slice(-8)) % 100_000_000, zero-padded to 8
 * Byte-identical with qr-rotation.js manualDigits on the backend. The digits
 * rotate with the same step/grace as the QR — a screenshot expires in ~60s.
 */
export const MANUAL_DIGIT_LEN = 8;

export async function computePersonalManualCode(
  code: string,
  qrSecret: string,
  nowMs = Date.now(),
): Promise<string> {
  const step = stepFor(nowMs);
  const sig = (await hmacHex(qrSecret, `${code}.${step}`)).slice(0, SIG_HEX_LEN);
  const n = parseInt(sig.slice(-8), 16) % 100_000_000;
  return String(n).padStart(MANUAL_DIGIT_LEN, '0');
}

export async function computePersonalQrPayload(
  code: string,
  qrSecret: string,
  nowMs = Date.now(),
): Promise<string> {
  const step = stepFor(nowMs);
  const sig = (await hmacHex(qrSecret, `${code}.${step}`)).slice(0, SIG_HEX_LEN);
  return `VQR1:${code}.${step}.${sig}`;
}

// ---------------------------------------------------------------------------
// VQR2 — user-identity rotating pass (ADDENDUM 2026-09-14b Phase 2 FE).
//
// One pass = the person: the payload identifies the USER, not an access_codes
// row. Backend serves the per-user secret via GET /api/qr-access/my-pass
// (user_qr_secret = HMAC-SHA256(JWT_SECRET, `user-pass:${user_id}`) — JWT_SECRET
// never reaches the client). The app signs locally, byte-identical with
// src/modules/qr-access/user-pass.js on the backend:
//   VQR2:<users.id>.<step>.<sig>
//   step = floor(now_ms / 60000)  (same 60s window / ±1 grace as VQR1)
//   sig  = HMAC-SHA256(user_qr_secret, `${user_id}.${step}`) truncated 16 hex
// Manual digits use the same sig (decimal of last 8 hex, % 1e8, zero-padded).
// ---------------------------------------------------------------------------

export const VQR2_PREFIX = 'VQR2:';

export async function computeUserPassPayload(
  userId: string,
  userQrSecret: string,
  nowMs = Date.now(),
): Promise<string> {
  const step = stepFor(nowMs);
  const sig = (await hmacHex(userQrSecret, `${userId}.${step}`)).slice(0, SIG_HEX_LEN);
  return `${VQR2_PREFIX}${userId}.${step}.${sig}`;
}

export async function computeUserPassManualCode(
  userId: string,
  userQrSecret: string,
  nowMs = Date.now(),
): Promise<string> {
  const step = stepFor(nowMs);
  const sig = (await hmacHex(userQrSecret, `${userId}.${step}`)).slice(0, SIG_HEX_LEN);
  const n = parseInt(sig.slice(-8), 16) % 100_000_000;
  return String(n).padStart(MANUAL_DIGIT_LEN, '0');
}
